import type { CSSProperties } from 'react';

import { useIsEnrolledInVariant } from 'hooks/unleash/useIsEnrolledInVariant';
import { FEATURE_PLP_PRODUCT_CARD_UI_UPDATES } from 'constants/features';
import {
  LEGACY_SWATCH_PADDING,
  LEGACY_SWATCH_SIZE,
  LEGACY_SWATCH_SIZE_WITH_PADDING,
  REDESIGN_SWATCH_PADDING,
  REDESIGN_SWATCH_SIZE,
  REDESIGN_SWATCH_SIZE_WITH_PADDING
} from 'constants/colorSwatchMap';

interface SwatchSizing {
  isPlpProductCardRedesignEnabled: boolean;
  SWATCH_SIZE: number;
  SWATCH_PADDING: number;
  SWATCH_SIZE_WITH_PADDING: number;
  swatchContainerStyle: CSSProperties;
  swatchListStyle: CSSProperties;
}

/**
 * Resolves the PLP product-card swatch geometry based on the FEE-8350 A/B test enrollment.
 *
 * Returns both the raw dimensions (used for the visible-swatch count calculation) and the
 * ready-to-spread CSS-var payloads (applied to the swatch container and list):
 *   - Treatment: 26px swatch + 3px padding → 32px footprint.
 *   - Control:   34px swatch + 4px padding → 42px footprint (current production).
 *
 * This hook is the single source of truth for swatch sizing. Consuming it in one place keeps the
 * parent component (ColorSwatches) and any future consumers that mirror the swatch footprint in
 * sync; add new size-related values here rather than branching on the flag at each call site.
 */
export const useSwatchSizing = (): SwatchSizing => {
  const { isEnrolled: isPlpProductCardRedesignEnabled } = useIsEnrolledInVariant({
    featureFlag: FEATURE_PLP_PRODUCT_CARD_UI_UPDATES,
    isZapposOnly: true
  });

  const SWATCH_SIZE = isPlpProductCardRedesignEnabled ? REDESIGN_SWATCH_SIZE : LEGACY_SWATCH_SIZE;
  const SWATCH_PADDING = isPlpProductCardRedesignEnabled ? REDESIGN_SWATCH_PADDING : LEGACY_SWATCH_PADDING;
  const SWATCH_SIZE_WITH_PADDING = isPlpProductCardRedesignEnabled ? REDESIGN_SWATCH_SIZE_WITH_PADDING : LEGACY_SWATCH_SIZE_WITH_PADDING;

  // Static CSS-var payloads for the swatch container and list.
  const swatchContainerStyle = {
    '--color-swatch-container-height': `${SWATCH_SIZE_WITH_PADDING}px`
  } as CSSProperties;

  const swatchListStyle = {
    '--color-swatch-size': `${SWATCH_SIZE}px`,
    '--color-swatch-x-padding': `${SWATCH_PADDING}px`,
    '--color-swatch-y-padding': `${SWATCH_PADDING}px`
  } as CSSProperties;

  return {
    isPlpProductCardRedesignEnabled,
    SWATCH_SIZE,
    SWATCH_PADDING,
    SWATCH_SIZE_WITH_PADDING,
    swatchContainerStyle,
    swatchListStyle
  };
};
